// src/controllers/useCrmReportsController.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import { leadService } from '../services/leadService';
import { useAuth } from './auth';

export const useCrmReportsController = () => {
  const { adminProfile } = useAuth();

  const [loading, setLoading] = useState(true);
  const [datePreset, setDatePreset] = useState('month'); // 'today', 'week', 'month', 'last30', 'all', 'custom'

  // Initialize dates to current month
  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return firstDay.toISOString().split('T')[0];
  });

  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  });

  const [staffPerformance, setStaffPerformance] = useState([]);
  const [conversionFunnel, setConversionFunnel] = useState([]);

  // Fetch performance and funnel data
  const fetchReportsData = useCallback(async () => {
    setLoading(true);
    try {
      let startIso = fromDate ? new Date(`${fromDate}T00:00:00.000Z`).toISOString() : null;
      let endIso = toDate ? new Date(`${toDate}T23:59:59.999Z`).toISOString() : null;

      const [staffRes, funnelRes] = await Promise.all([
        leadService.getCrmStaffPerformanceReport(startIso, endIso),
        leadService.getCrmConversionFunnel(startIso, endIso)
      ]);

      setStaffPerformance(staffRes.data || []);
      setConversionFunnel(funnelRes.data || []);
    } catch (err) {
      console.error('Error fetching CRM reports:', err);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    fetchReportsData();
  }, [fetchReportsData]);

  // Handle Preset Change
  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    const now = new Date();

    if (preset === 'today') {
      const todayStr = now.toISOString().split('T')[0];
      setFromDate(todayStr);
      setToDate(todayStr);
    } else if (preset === 'week') {
      const past7 = new Date();
      past7.setDate(past7.getDate() - 7);
      setFromDate(past7.toISOString().split('T')[0]);
      setToDate(now.toISOString().split('T')[0]);
    } else if (preset === 'month') {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      setFromDate(firstDay.toISOString().split('T')[0]);
      setToDate(now.toISOString().split('T')[0]);
    } else if (preset === 'last30') {
      const past30 = new Date();
      past30.setDate(past30.getDate() - 30);
      setFromDate(past30.toISOString().split('T')[0]);
      setToDate(now.toISOString().split('T')[0]);
    } else if (preset === 'all') {
      setFromDate('');
      setToDate('');
    }
  };

  // Summary KPIs for current range
  const summaryKPIs = useMemo(() => {
    const totalAssigned = staffPerformance.reduce((acc, s) => acc + Number(s.total_assigned || 0), 0);
    const totalWon = staffPerformance.reduce((acc, s) => acc + Number(s.deals_won || 0), 0);
    const totalLost = staffPerformance.reduce((acc, s) => acc + Number(s.deals_lost || 0), 0);
    const totalSiteVisits = staffPerformance.reduce((acc, s) => acc + Number(s.site_visits || 0), 0);
    const totalActive = staffPerformance.reduce((acc, s) => acc + Number(s.active_leads || 0), 0);

    const overallConversionRate = totalAssigned > 0 
      ? Math.round((totalWon / totalAssigned) * 1000) / 10 
      : 0;

    return {
      totalAssigned,
      totalWon,
      totalLost,
      totalSiteVisits,
      totalActive,
      overallConversionRate
    };
  }, [staffPerformance]);

  // Export Staff Performance CSV
  const handleExportStaffPerformanceCSV = () => {
    if (staffPerformance.length === 0) {
      alert('No staff performance data to export');
      return;
    }

    const headers = [
      'Staff Member',
      'Role',
      'Total Leads Assigned',
      'Active Pipeline Leads',
      'Site Visits Conducted',
      'Deals Won (Closed)',
      'Deals Lost',
      'Conversion Rate (%)'
    ];

    const rows = staffPerformance.map(s => [
      `"${(s.staff_name || 'Staff').replace(/"/g, '""')}"`,
      `"${(s.role || '').replace(/"/g, '""')}"`,
      s.total_assigned || 0,
      s.active_leads || 0,
      s.site_visits || 0,
      s.deals_won || 0,
      s.deals_lost || 0,
      `${s.conversion_rate_pct || 0}%`
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,'
      + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `staff_performance_report_${fromDate || 'start'}_to_${toDate || 'today'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export Conversion Funnel CSV
  const handleExportFunnelCSV = () => {
    if (conversionFunnel.length === 0) {
      alert('No funnel data to export');
      return;
    }

    const headers = [
      'Pipeline Stage Order',
      'Pipeline Stage Name',
      'Total Leads in Stage',
      'Percentage of Total Leads (%)'
    ];

    const rows = conversionFunnel.map(f => [
      f.stage_order,
      `"${(f.stage || '').replace(/"/g, '""')}"`,
      f.leads_count || 0,
      `${f.pct_of_total || 0}%`
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,'
      + [headers.join(','), ...rows.map(e => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `crm_conversion_funnel_${fromDate || 'start'}_to_${toDate || 'today'}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return {
    loading,
    datePreset,
    setDatePreset,
    handlePresetChange,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    staffPerformance,
    conversionFunnel,
    summaryKPIs,
    handleExportStaffPerformanceCSV,
    handleExportFunnelCSV,
    fetchReportsData,
    adminProfile
  };
};

